import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LUNAR } from '../constants/testIds';
import { Lock, Mail, User, Shield, CheckCircle, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Auth() {
  const { user, login, register, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [isRegister, setIsRegister] = useState(false);
  
  // Input fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Status state
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isRegister) {
        await register(name, email, password);
        toast.success('Registration Complete', {
          description: 'Your secure cosmic collector credentials have been generated.'
        });
        navigate('/dashboard');
      } else {
        const userData = await login(email, password);
        toast.success('Authenticated Successfully', {
          description: 'Welcome back to the safe control room, collector.'
        });
        navigate(userData?.role === 'admin' ? '/admin' : '/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Authentication failed. Please verify credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  // Pre-fill helper for test users
  const handlePreFill = (type) => {
    setError('');
    if (type === 'admin') {
      setEmail('admin@example.com');
      setPassword('admin123');
      setName('Director Alexander');
      toast.info('Admin Credentials Loaded', { description: 'Click sign in below to enter Curator mode.' });
    } else {
      setEmail('collector@example.com');
      setPassword('collector123');
      setName('Commander Sterling');
      toast.info('Collector Credentials Loaded', { description: 'Click sign in below to enter standard collector panel.' });
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-luxury-black text-white flex items-center justify-center font-serif-luxury text-luxury-gold tracking-widest animate-pulse">
        VALIDATING TRANSIT SECURITY...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-luxury-black text-white flex items-center justify-center py-12 px-6 md:px-12">
      <div className="max-w-md w-full relative">
        
        {/* Background decorative luxury element */}
        <div className="absolute inset-0 bg-gradient-to-tr from-luxury-gold/5 via-transparent to-transparent -z-10 filter blur-3xl"></div>

        {/* Auth Card */}
        <div className="border border-white/10 bg-luxury-charcoal p-8 md:p-10 shadow-2xl relative" data-testid={isRegister ? LUNAR.registerCard : LUNAR.loginCard}>
          
          {/* Logo Heading */}
          <div className="text-center mb-8">
            <div className="w-10 h-10 border border-luxury-gold flex items-center justify-center bg-black mx-auto mb-4">
              <span className="text-luxury-gold font-serif-luxury font-bold text-xl">L</span>
            </div>
            <h2 className="text-2xl font-serif-luxury text-white">
              {isRegister ? 'Create Your Account' : 'Sign In'}
            </h2>
            <p className="text-xs text-luxury-muted mt-2 font-sans-luxury">
              {isRegister ? 'Create an account to save items and submit consignments.' : 'Access your saved items and consignment inquiries.'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-1.5">Curator Title / Name</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-luxury-muted">
                    <User size={14} />
                  </span>
                  <input 
                    type="text" 
                    required 
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Commander Neil Armstrong"
                    className="w-full bg-black border border-white/10 text-white placeholder-white/20 focus:border-luxury-gold text-xs pl-10 pr-4 py-3 outline-none rounded-none transition-colors duration-300"
                    data-testid={LUNAR.registerNameInput}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-1.5">Email</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-luxury-muted">
                  <Mail size={14} />
                </span>
                <input 
                  type="email" 
                  required 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. neil@apollo11.nasa.gov"
                  className="w-full bg-black border border-white/10 text-white placeholder-white/20 focus:border-luxury-gold text-xs pl-10 pr-4 py-3 outline-none rounded-none transition-colors duration-300"
                  data-testid={isRegister ? LUNAR.registerEmailInput : LUNAR.loginEmailInput}
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-1.5">Passkey</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-luxury-muted">
                  <Lock size={14} />
                </span>
                <input 
                  type="password" 
                  required 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-black border border-white/10 text-white placeholder-white/20 focus:border-luxury-gold text-xs pl-10 pr-4 py-3 outline-none rounded-none transition-colors duration-300"
                  data-testid={isRegister ? LUNAR.registerPasswordInput : LUNAR.loginPasswordInput}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs p-3 font-sans-luxury" data-testid={isRegister ? LUNAR.registerErrorMessage : LUNAR.loginErrorMessage}>
                {error}
              </div>
            )}

            <button 
              type="submit" 
              disabled={isLoading}
              className="btn-gold w-full font-sans-luxury uppercase tracking-[0.2em] text-xs font-semibold py-3.5 rounded-none flex items-center justify-center gap-1.5"
              data-testid={isRegister ? LUNAR.registerSubmitBtn : LUNAR.loginSubmitBtn}
            >
              {isLoading ? (
                <>
                  <Loader2 size={12} className="animate-spin text-black" /> Please wait...
                </>
              ) : (
                isRegister ? 'Create Account' : 'Sign In'
              )}
            </button>
          </form>

          {/* Quick Demo Pre-fills (Extremely helpful for testing and verification!) */}
          {!isRegister && (
            <div className="mt-6 border-t border-white/5 pt-5 text-center">
              <span className="text-[9px] uppercase tracking-widest text-luxury-muted font-bold block mb-3">Testing Access</span>
              <div className="flex gap-3 justify-center">
                <button 
                  onClick={() => handlePreFill('collector')}
                  className="text-[9px] bg-white/5 hover:bg-white/10 border border-white/10 text-luxury-goldLight px-3 py-2 uppercase tracking-widest transition-all duration-300"
                >
                  Collector Access
                </button>
                <button 
                  onClick={() => handlePreFill('admin')}
                  className="text-[9px] bg-white/5 hover:bg-white/10 border border-white/10 text-luxury-gold px-3 py-2 uppercase tracking-widest transition-all duration-300"
                >
                  Admin Access
                </button>
              </div>
            </div>
          )}

          {/* Toggle between Register/Login */}
          <div className="mt-6 text-center text-xs font-sans-luxury">
            <span className="text-luxury-muted">
              {isRegister ? 'Already have an account?' : 'Need an account?'}
            </span>{' '}
            <button 
              onClick={() => {
                setIsRegister(!isRegister);
                setError('');
              }}
              className="text-luxury-gold hover:text-luxury-goldLight hover:underline font-bold transition-colors ml-1"
              data-testid={isRegister ? LUNAR.registerToggleLogin : LUNAR.loginToggleRegister}
            >
              {isRegister ? 'Sign In' : 'Register Here'}
            </button>
          </div>

        </div>

      </div>
    </div>
  );
}
